
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import SimpleLoader from '../components/SimpleLoader';
import { getFileOpeningsWithBalance } from '../services/fileOpenings';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import TableContainer from '../components/TableContainer';

const LedgerList = () => {
    const [fileOpenings, setFileOpenings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [limit, setLimit] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchFileOpenings = async () => {
            try {
                const offset = (currentPage - 1) * limit;
                const { fileOpenings, count } = await getFileOpeningsWithBalance({
                    limit,
                    offset,
                    sortBy,
                    sortOrder,
                    searchTerm
                });
                setFileOpenings(fileOpenings);
                setTotal(count);
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        const delayDebounceFn = setTimeout(() => {
            fetchFileOpenings();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [limit, currentPage, sortBy, sortOrder, searchTerm]);

    const handleLimitChange = (e) => {
        setLimit(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleSortByChange = (e) => {
        const [newSortBy, newSortOrder] = e.target.value.split(':');
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const getBalanceBadge = (balance) => {
        if (balance === 0) {
            return (
                <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-black">
                    £{balance?.toFixed(2)}
                </span>
            );
        } else if (balance > 0) {
            return (
                <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-black">
                    £{balance?.toFixed(2)}
                </span>
            );
        } else {
            return (
                <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-white">
                    £{Math.abs(balance)?.toFixed(2)}
                </span>
            );
        }
    };

    const getPaymentStatus = (balance) => {
        if (balance === 0) {
            return (
                <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-black">
                    Paid
                </span>
            );
        } else if (balance > 0) {
            return (
                <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-black">
                    Pending
                </span>
            );
        } else {
            return (
                <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-white">
                    Credit
                </span>
            );
        }
    };

    const getClientName = (file) => {
        return `${file.title || ''} ${file.forename} ${file.surname}`.trim();
    };

    return (
        <DashboardLayout pageTitle="Ledger - All File Openings">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                    <input
                        type="text"
                        placeholder="Search clients..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    />
                    <select
                        value={limit}
                        onChange={handleLimitChange}
                        className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <select
                        value={`${sortBy}:${sortOrder}`}
                        onChange={handleSortByChange}
                        className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    >
                        <option value="id:desc">ID (Z-A)</option>
                        <option value="id:asc">ID (A-Z)</option>
                        <option value="surname:asc">Surname (A-Z)</option>
                        <option value="surname:desc">Surname (Z-A)</option>
                        <option value="balance:desc">Balance (Highest First)</option>
                        <option value="balance:asc">Balance (Lowest First)</option>
                        <option value="total_invoices:desc">Invoiced (Highest First)</option>
                        <option value="total_invoices:asc">Invoiced (Lowest First)</option>
                        <option value="total_received:desc">Received (Highest First)</option>
                        <option value="total_received:asc">Received (Lowest First)</option>
                    </select>
                </div>
                <div className="flex space-x-2">
                    <Link
                        to="/file-opening/create"
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow"
                    >
                        New File
                    </Link>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Total Files</div>
                    <div className="text-2xl font-bold text-gray-800 dark:text-white">{total}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Total Invoiced</div>
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        £{fileOpenings.reduce((sum, file) => sum + (file.total_invoices || 0), 0).toFixed(2)}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Total Received</div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        £{fileOpenings.reduce((sum, file) => sum + (file.total_received || 0), 0).toFixed(2)}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Outstanding</div>
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                        £{fileOpenings.reduce((sum, file) => sum + (file.balance || 0), 0).toFixed(2)}
                    </div>
                </div>
            </div>

            <TableContainer>
                {loading ? (
                    <SimpleLoader />
                ) : (
                    <>
                        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3">File #</th>
                                    <th scope="col" className="px-6 py-3">Client</th>
                                    <th scope="col" className="px-6 py-3">Total Invoiced</th>
                                    <th scope="col" className="px-6 py-3">Total Received</th>
                                    <th scope="col" className="px-6 py-3">Balance</th>
                                    <th scope="col" className="px-6 py-3">Status</th>
                                    <th scope="col" className="px-6 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fileOpenings.map((file) => (
                                    <tr key={file.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                                            {file.id}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900 dark:text-white">
                                                {getClientName(file)}
                                            </div>
                                            {file.phone && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {file.phone}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 font-semibold">
                                            £{file.total_invoices?.toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 font-semibold text-green-600 dark:text-green-400">
                                            £{file.total_received?.toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {getBalanceBadge(file.balance)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {getPaymentStatus(file.balance)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Link
                                                to={`/file-opening/${file.id}`}
                                                className="bg-blue-600  hover:bg-blue-800 rounded p-2 text-xs text-white mr-4"
                                            >
                                                File Details
                                            </Link>
                                            <Link
                                                to={`/file-opening/${file.id}/invoices`}
                                                className="bg-blue-600 hover:bg-blue-800 rounded p-2 text-xs text-white mr-4"
                                            >
                                                Invoices
                                            </Link>
                                            <Link
                                                to={`/file-opening/${file.id}/receipts`}
                                                className="bg-blue-600 hover:bg-blue-800 rounded p-2 text-xs text-white mr-4"
                                            >
                                                Receipts
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                                {fileOpenings.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                            No file openings found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <Pagination
                            currentPage={currentPage}
                            total={total}
                            limit={limit}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}
            </TableContainer>
        </DashboardLayout>
    );
};

export default LedgerList;